import React from "react";

/**
 * DossierLabel — the DEFAULT subsection heading inside a dossier.
 *
 * Renders as:    // EXECUTIVE SUMMARY
 *
 * Use this for every regular section heading. It is intentionally low-key
 * and consistent so the red-stamp callout (DossierStamp) stays visually
 * distinct for special-emphasis blocks.
 *
 * Usage:
 *   <DossierLabel>Executive Summary</DossierLabel>
 *   <DossierLabel as="h2" align="left">The Hidden Truth</DossierLabel>
 *
 * Props:
 *   children — the label text (auto-uppercased)
 *   as       — semantic tag, default "h2"
 *   align    — "left" (default) | "center"
 *   icon     — optional lucide-react icon (rendered before text)
 *   tone     — "red" (default, brand accent) | "muted" (subtle stone tone)
 *   className — additional Tailwind classes
 */
export const DossierLabel = ({
  children,
  as: Tag = "h2",
  align = "left",
  icon: Icon,
  tone = "red",
  className = "",
  ...rest
}) => {
  const color = tone === "muted" ? "text-[#A39E93]" : "text-[#D92D20]";
  const justify = align === "center" ? "justify-center text-center" : "justify-start";
  return (
    <Tag
      data-dossier-label
      className={
        "flex items-center gap-2 font-mono text-[10px] sm:text-[11px] " +
        "uppercase tracking-[0.3em] leading-snug " +
        "mt-10 mb-3 sm:mb-4 " +
        `${color} ${justify} ${className}`
      }
      {...rest}
    >
      <span aria-hidden className="opacity-80">//</span>
      {Icon ? <Icon size={12} className="shrink-0" /> : null}
      <span className="break-words">{children}</span>
    </Tag>
  );
};

export default DossierLabel;
